import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type DragEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { Icon } from '@/components/Icon';
import { kstTodayYmd, kstYmdHyphen } from '@/lib/format-date';
import { burstSparks, prefersReducedMotion, robotImageFor } from '@/pages/robots/robot-board';

// 로봇 배정 '장착대'(2026-11-04 오너 — 시안 5 "5번으로 하고, 그냥 클릭만 해도 여러개 잡을 수 있게 하자.
// 드래그로 여러개 잡을 수도 있게 하고"). 로봇 상세 부품 장착(PartInstallBoard · robot-board.tsx)과 같은
// 짜임과 움직임이다.
//   · AssignPickList — 왼쪽 창고 로봇. 누르면 고르고 다시 누르면 풀린다(Ctrl 없이 여러 대). 빈 곳에서 끌면
//     선택 상자로 여러 대를 더 고른다. 고른 것을 끌면 고른 것 전부, 안 고른 것을 끌면 그 한 대만 간다.
//   · AssignStage — 가운데 무대. 계약 줄마다 필요한 대수만큼 로봇 그림이 자리로 선다. 끄는 동안 맞는
//     빈자리가 떠오르고, 올리면 빛나며, 놓으면 번호표가 그 자리로 빨려 든다. 저장하면 불꽃.
// 무엇을 어느 자리에 그릴지는 페이지가 정해 넘긴다 — 여기는 그리기와 끌어 놓기만 맡는다.

/** 끌어 옮기는 값의 종류 — 다른 화면의 끌기(부품 등)와 섞이지 않게 따로 둔다. */
export const ASSIGN_DRAG_TYPE = 'application/x-bhops-assign';

/** 끌어 옮기는 것 — 재고 로봇 여러 대와 제조 계획(한 번에 한 대씩). */
export interface DragLoad {
  robotIds: string[];
  planIds: string[];
}

// 선택 상자가 이만큼은 움직여야 '끌기'로 본다 — 그보다 짧으면 빈 곳 클릭(선택 해제).
const MARQUEE_THRESHOLD = 4;
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';

// ── 위: 고객사 · 이벤트 카드 ─────────────────────────────────────────────

/** 설치일 — 카드의 큰 날짜(MM.DD)와 남은 날. 한국 날짜로 센다. */
export function dayInfo(iso: string | null | undefined): {
  big: string;
  left: string;
  days: number | null;
} {
  const ymd = iso ? kstYmdHyphen(iso) : null;
  if (!ymd) return { big: '미정', left: '설치일 미정', days: null };
  const today = kstTodayYmd();
  const days = Math.round(
    (Date.parse(`${ymd}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  return {
    big: `${ymd.slice(5, 7)}.${ymd.slice(8, 10)}`,
    left: days === 0 ? '오늘' : days > 0 ? `${days}일 남음` : `${-days}일 지남`,
    days,
  };
}

/** 설치가 닷새 안이면 남은 날을 붉게(배정을 마쳤으면 그러지 않는다). */
export const isSoon = (days: number | null, done: boolean) => !done && days !== null && days <= 5;

export function StripCard({
  testId,
  headerTestId,
  selected,
  gated = false,
  onSelect,
  date,
  dateNote,
  done,
  name,
  sub,
  assigned,
  required,
  badge,
  notes,
}: {
  testId: string;
  headerTestId?: string;
  selected: boolean;
  /** 고객사 상태가 배정 불가 — 흐리고 누르면 막는다(누르기는 onSelect 가 판정). */
  gated?: boolean;
  onSelect: () => void;
  date: string | null;
  /** 큰 날짜 아래 작은 글 — 주지 않으면 남은 날. */
  dateNote?: string;
  done: boolean;
  name: ReactNode;
  sub: ReactNode;
  assigned: number;
  required: number;
  badge: ReactNode;
  notes?: ReactNode;
}) {
  const d = dayInfo(date);
  return (
    <div
      data-testid={testId}
      className="asg-card"
      data-selected={selected || undefined}
      data-gated={gated || undefined}
      style={{ opacity: gated ? 0.55 : 1 }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-disabled={gated}
        aria-pressed={selected}
        data-testid={headerTestId}
        className="asg-card-main"
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        <span className="asg-card-date" data-soon={isSoon(d.days, done || gated) || undefined}>
          {d.big}
          <small>{dateNote ?? d.left}</small>
        </span>
        <span className="asg-card-name">{name}</span>
        <span className="asg-card-sub">{sub}</span>
        <span className="asg-card-meter">
          <i aria-hidden>
            <b style={{ width: `${required > 0 ? Math.min(1, assigned / required) * 100 : 0}%` }} />
          </i>
          <span className="num">
            {assigned}/{required}
          </span>
          {badge}
        </span>
      </div>
      {notes}
    </div>
  );
}

// ── 왼쪽: 창고 로봇 ─────────────────────────────────────────────────────

export interface PickRow {
  /** 'r:<로봇 id>' · 'p:<제조 계획 id>' — 페이지가 정한다. */
  id: string;
  /** 끌 때 커서에 붙는 이름(#230 · PP-2026-0007). */
  label: string;
  node: ReactNode;
  disabled?: boolean;
  /** 제조 계획은 빗금, 늦는 계획은 흐리게. */
  tone?: 'plan' | 'late';
  title?: string;
  testId?: string;
}

export interface PickSection {
  key: string;
  title?: ReactNode;
  rows: PickRow[];
  /** 묶음 아래에 붙는 안내(숨긴 대수 등). */
  after?: ReactNode;
}

export function AssignPickList({
  sections,
  picked,
  onPickedChange,
  onPut,
  loadOf,
  onDragLoad,
  canDrag,
  empty,
  testId,
}: {
  sections: PickSection[];
  picked: Set<string>;
  /** 함수형 갱신을 받는다 — 빠르게 연달아 눌러도 앞서 고른 것을 덮어쓰지 않게. */
  onPickedChange: Dispatch<SetStateAction<Set<string>>>;
  /** 두 번 누르기 — 그 한 대를 맞는 빈자리에 바로 넣는다. */
  onPut: (ids: string[]) => void;
  loadOf: (ids: string[]) => DragLoad;
  /** 끌기 시작 · 끝 — 무대가 맞는 자리를 띄워 기다린다. */
  onDragLoad: (load: DragLoad | null) => void;
  canDrag: boolean;
  empty?: ReactNode;
  testId: string;
}) {
  const [held, setHeld] = useState<Set<string>>(() => new Set());
  const [marquee, setMarquee] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<{ x0: number; y0: number; base: Set<string>; moved: boolean } | null>(
    null,
  );
  const rows = useMemo(() => sections.flatMap((s) => s.rows), [sections]);
  const labelOf = (id: string) => rows.find((r) => r.id === id)?.label ?? '';

  function contentPoint(clientX: number, clientY: number): { x: number; y: number } {
    const el = listRef.current!;
    const r = el.getBoundingClientRect();
    return { x: clientX - r.left + el.scrollLeft, y: clientY - r.top + el.scrollTop };
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>): void {
    const el = listRef.current;
    if (!el || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('[data-pick-chip], button, a, input, label')) return;
    // 스크롤 막대를 잡은 경우는 선택 상자를 만들지 않는다.
    const r = el.getBoundingClientRect();
    if (el.clientWidth > 0 && e.clientX - r.left > el.clientWidth) return;
    const pt = contentPoint(e.clientX, e.clientY);
    // 누르기가 '더하기'라 선택 상자도 이미 고른 것에 더한다.
    marqueeRef.current = { x0: pt.x, y0: pt.y, base: new Set(picked), moved: false };
    el.setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>): void {
    const m = marqueeRef.current;
    const el = listRef.current;
    if (!m || !el) return;
    // 목록 위아래로 벗어나면 그쪽으로 굴려 가며 이어서 고른다.
    const r = el.getBoundingClientRect();
    if (e.clientY < r.top) el.scrollTop -= 12;
    else if (e.clientY > r.bottom) el.scrollTop += 12;

    const pt = contentPoint(e.clientX, e.clientY);
    if (
      !m.moved &&
      Math.abs(pt.x - m.x0) < MARQUEE_THRESHOLD &&
      Math.abs(pt.y - m.y0) < MARQUEE_THRESHOLD
    ) {
      return;
    }
    m.moved = true;
    const box = {
      left: Math.min(m.x0, pt.x),
      top: Math.min(m.y0, pt.y),
      width: Math.abs(pt.x - m.x0),
      height: Math.abs(pt.y - m.y0),
    };
    setMarquee(box);

    const next = new Set(m.base);
    el.querySelectorAll<HTMLElement>('[data-pick-chip]').forEach((chip) => {
      if (chip.getAttribute('aria-disabled') === 'true') return;
      const c = chip.getBoundingClientRect();
      const left = c.left - r.left + el.scrollLeft;
      const top = c.top - r.top + el.scrollTop;
      if (
        left < box.left + box.width &&
        left + c.width > box.left &&
        top < box.top + box.height &&
        top + c.height > box.top
      ) {
        next.add(chip.dataset.pickChip!);
      }
    });
    onPickedChange(next);
  }

  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>): void {
    const m = marqueeRef.current;
    if (!m) return;
    marqueeRef.current = null;
    setMarquee(null);
    listRef.current?.releasePointerCapture?.(e.pointerId);
    // 빈 곳을 그냥 누르면 고른 것을 모두 푼다.
    if (!m.moved) onPickedChange(new Set());
  }

  function toggle(id: string): void {
    onPickedChange((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onChipDragStart(e: DragEvent<HTMLButtonElement>, row: PickRow): void {
    // 고른 것을 잡으면 고른 것 전부, 안 고른 것을 잡으면 그것 하나만 끈다.
    const ids = picked.has(row.id)
      ? rows.filter((r) => picked.has(r.id)).map((r) => r.id)
      : [row.id];
    const load = loadOf(ids);
    e.dataTransfer.setData(ASSIGN_DRAG_TYPE, JSON.stringify(load));
    e.dataTransfer.effectAllowed = 'copy';
    // 커서에 붙는 그림 — 한 대면 번호, 여러 대면 겹친 카드에 대수(부품 장착과 같음).
    const ghost = ghostRef.current;
    if (ghost && e.dataTransfer.setDragImage) {
      ghost.textContent = ids.length > 1 ? `${ids.length}대` : labelOf(row.id);
      ghost.dataset.stack = String(Math.min(ids.length, 3));
      e.dataTransfer.setDragImage(ghost, 18, 18);
    }
    setHeld(new Set(ids));
    onDragLoad(load);
  }

  return (
    <>
      <div
        ref={listRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="listbox"
        aria-multiselectable
        aria-label="창고 로봇"
        data-testid={testId}
        className="asg-pick-list"
      >
        {rows.length === 0 && empty}
        {sections.map((s) =>
          s.rows.length === 0 && !s.after ? null : (
            <div key={s.key} className="asg-pick-section">
              {s.title && <div className="asg-pick-group">{s.title}</div>}
              {s.rows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  role="option"
                  aria-selected={picked.has(row.id)}
                  aria-disabled={row.disabled || undefined}
                  className="pib-chip asg-chip"
                  data-pick-chip={row.id}
                  data-tone={row.tone}
                  data-held={held.has(row.id) || undefined}
                  data-testid={row.testId}
                  title={row.title}
                  draggable={canDrag && !row.disabled}
                  onClick={() => {
                    if (!row.disabled) toggle(row.id);
                  }}
                  onDoubleClick={() => {
                    if (!row.disabled && canDrag) onPut([row.id]);
                  }}
                  onDragStart={(e) => onChipDragStart(e, row)}
                  onDragEnd={() => {
                    setHeld(new Set());
                    onDragLoad(null);
                  }}
                >
                  {row.node}
                  <span aria-hidden className="pib-check">
                    <Icon name="check" size={11} />
                  </span>
                </button>
              ))}
              {s.after}
            </div>
          ),
        )}
        {marquee && (
          <div
            aria-hidden
            className="pib-marquee"
            data-testid={`${testId}-marquee`}
            style={{ position: 'absolute', ...marquee, pointerEvents: 'none' }}
          />
        )}
      </div>
      {/* 끄는 동안 커서에 붙는 그림 — 화면 밖에 두고 setDragImage 로만 쓴다. */}
      <div ref={ghostRef} aria-hidden className="pib-ghost" />
    </>
  );
}

/** 창고 로봇 칸의 틀 — 머리(대수 · [n대 넣기]), 번호 찾기 · 완전일치만 보기, 안내, 목록. */
export function PickPanel({
  count,
  pickedCount,
  onPutPicked,
  keyword,
  onKeyword,
  exactOnly,
  onExactOnly,
  showTools = true,
  children,
}: {
  count: number;
  pickedCount: number;
  onPutPicked: () => void;
  keyword: string;
  onKeyword: (v: string) => void;
  /** 주지 않으면 체크 칸을 그리지 않는다(이벤트는 희망 사양이 없다). */
  exactOnly?: boolean;
  onExactOnly?: (v: boolean) => void;
  /** 목록이 아니라 안내만 보일 때는 찾기 칸을 감춘다. */
  showTools?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="asg-panel asg-pick" data-testid="assign-robot-panel" aria-label="창고 로봇">
      <div className="asg-panel-head">
        <span className="asg-panel-title">
          창고 로봇 <span style={{ color: 'var(--fg-muted)', fontWeight: 500 }}>({count})</span>
        </span>
        {pickedCount > 0 && (
          <button
            type="button"
            className="asg-btn"
            data-variant="primary"
            data-size="sm"
            style={{ marginLeft: 'auto' }}
            onClick={onPutPicked}
            data-testid="assign-put-picked"
          >
            {pickedCount}대 넣기
          </button>
        )}
      </div>
      {showTools && (
        <>
          <div className="asg-pick-tools">
            <input
              type="search"
              className="asg-field"
              placeholder="번호로 찾기"
              aria-label="로봇 번호로 찾기"
              value={keyword}
              onChange={(e) => onKeyword(e.target.value)}
            />
            {onExactOnly && (
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!exactOnly}
                  onChange={(e) => onExactOnly(e.target.checked)}
                />
                완전일치만 보기
              </label>
            )}
          </div>
          <div className="asg-pick-help">
            누르면 고르고 다시 누르면 풀립니다. 빈 곳에서 끌면 여러 대를 한꺼번에 고릅니다.
          </div>
        </>
      )}
      <div className="asg-pick-body" data-testid="assign-robot-scroll" style={{ overflow: 'auto' }}>
        {children}
      </div>
    </section>
  );
}

// ── 가운데: 무대 ─────────────────────────────────────────────────────────

/** 자리 하나의 판정 색 — ok 맞음 · warn 일부 · bad 다름 · plain 판정 없음. */
export type SlotTone = 'ok' | 'warn' | 'bad' | 'plain';
/** 끄는 동안 그 줄이 받을 만한가 — off 는 모델이 달라 못 받는다. */
export type LineHint = 'ok' | 'warn' | 'bad' | 'off';

export interface StageSlot {
  key: string;
  kind: 'empty' | 'robot' | 'plan';
  /** 아직 저장하지 않은 자리. */
  draft?: boolean;
  tone?: SlotTone;
  plate?: ReactNode;
  sub?: ReactNode;
  /** 번호표 아래 덧붙는 표시(요청과 다름 · 상태 등). */
  extra?: ReactNode;
  /** 오른쪽 위 단추(빼기 · 교체). */
  action?: ReactNode;
  /** 이 자리에 놓으면 바꾸기(저장된 로봇 자리). */
  replaceable?: boolean;
  /** 날아드는 번호표의 짝 — 'robot:<id>' · 'plan:<id>'. */
  flyKey?: string;
  /** 빈자리를 누르면(고른 로봇을 넣는다). */
  onClick?: () => void;
  title?: string;
  testId?: string;
}

/**
 * 줄 위의 큰 제목(2026-11-04 오너 "옵션에 right가 있으면 정방향, left가 있으면 역방향이라고 위에 적혀있는게
 * 좋겠다. 없으면 방향 미정 이라고 적어두고", "제퍼는 그냥 제퍼라고 적어두고").
 * 구분 축이 '방향'이 아닌 모델(GEN1 계열 '로더 통신 케이블')은 방향으로 부를 수 없어 제목을 달지 않는다.
 */
export function lineHeading(
  model: string | null | undefined,
  direction: string | null | undefined,
  variantAxisLabel?: string | null,
): string | null {
  const m = (model ?? '').trim().toUpperCase();
  if (m.startsWith('ZEPHYR') || m.includes('제퍼')) return '제퍼';
  if (m.startsWith('GEN1')) return null;
  if (variantAxisLabel && variantAxisLabel.trim() !== '방향') return null;
  const d = (direction ?? '').trim().toLowerCase();
  if (d === 'right') return '정방향';
  if (d === 'left') return '역방향';
  return '방향 미정';
}

/**
 * 창고 로봇 목록의 순서 — 로봇 번호 순(2026-11-04 오너 "정렬 조건을 로봇 넘버순으로 하자").
 * 번호는 글자가 아니라 숫자로 견준다(343 < 1000, 124 < 124-2). 앞의 '#' 는 떼고 본다.
 * 번호가 없는 로봇은 맨 뒤로 보낸다.
 */
export function compareRobotNumber(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const x = (a ?? '').trim().replace(/^#/, '');
  const y = (b ?? '').trim().replace(/^#/, '');
  if (!x || !y) return Number(!x) - Number(!y);
  return x.localeCompare(y, 'ko', { numeric: true });
}

export interface StageLine {
  key: string;
  modelCode: string | null;
  /** 줄 위의 큰 제목(정방향 · 역방향 · 방향 미정 · 제퍼) — lineHeading. */
  heading?: string | null;
  title: ReactNode;
  spec?: ReactNode;
  count: ReactNode;
  hint?: LineHint;
  slots: StageSlot[];
}

export interface FlyRequest {
  seq: number;
  /** 이름표가 떠날 자리 — 놓은 곳이거나 누른 칩. 모르면 null(날리지 않는다). */
  from: (label: string) => { x: number; y: number } | null;
  targets: Array<{ flyKey: string; label: string }>;
}

export interface StageHandle {
  /** 저장 성공 — 무대 가운데서 불꽃이 퍼진다. */
  burst: () => void;
}

export const AssignStage = forwardRef<
  StageHandle,
  {
    header: ReactNode;
    notes?: ReactNode;
    lines: StageLine[];
    /** 줄이 없을 때(고객사를 안 골랐을 때 등) 무대 가운데 문구. */
    empty?: ReactNode;
    hint?: ReactNode;
    footer?: ReactNode;
    canDrop: boolean;
    dragging: boolean;
    onDrop: (load: DragLoad, slot: StageSlot | null, point: { x: number; y: number }) => void;
    flyRequest: FlyRequest | null;
    testId: string;
  }
>(function AssignStage(
  { header, notes, lines, empty, hint, footer, canDrop, dragging, onDrop, flyRequest, testId },
  ref,
) {
  const [overKey, setOverKey] = useState<string | null>(null);
  const stageRef = useRef<HTMLElement>(null);
  const floorRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    burst: () => burstSparks(stageRef.current, floorRef.current),
  }));

  // 놓은 뒤 — 새 자리가 그려진 바로 다음에 이름표를 날린다(그 전엔 날아갈 곳이 없다).
  useLayoutEffect(() => {
    if (!flyRequest || prefersReducedMotion()) return;
    const floor = floorRef.current;
    if (!floor || typeof floor.animate !== 'function') return;
    flyRequest.targets.slice(0, 6).forEach(({ flyKey, label }, i) => {
      const all = floor.querySelectorAll<HTMLElement>(`[data-fly="${CSS.escape(flyKey)}"]`);
      const el = all[all.length - 1];
      const src = flyRequest.from(label);
      if (!el || !src) return;
      const t = el.getBoundingClientRect();
      const tx = t.left + t.width / 2;
      const ty = t.top + t.height * 0.42;
      const f = document.createElement('div');
      f.className = 'pib-flyer';
      f.textContent = label;
      f.style.left = `${src.x}px`;
      f.style.top = `${src.y}px`;
      document.body.appendChild(f);
      f.animate(
        [
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
          {
            transform: `translate(-50%, -50%) translate(${tx - src.x}px, ${ty - src.y}px) scale(0.45)`,
            opacity: 0,
          },
        ],
        { duration: 420, delay: i * 50, easing: EASE_IN_OUT, fill: 'both' },
      ).onfinish = () => f.remove();
      (el.querySelector('img') ?? el).animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(0.96, 1.03)', offset: 0.35 },
          { transform: 'scale(1.03, 0.98)', offset: 0.65 },
          { transform: 'scale(1)' },
        ],
        { duration: 420, delay: 300 + i * 50, easing: EASE_OUT },
      );
    });
    // seq 가 바뀔 때만 — 같은 요청을 다시 날리지 않는다.
  }, [flyRequest?.seq]);

  const accepts = (e: DragEvent): boolean =>
    canDrop && Array.from(e.dataTransfer.types).includes(ASSIGN_DRAG_TYPE);

  function onDragOver(e: DragEvent<HTMLDivElement>): void {
    if (!accepts(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const key =
      (e.target as HTMLElement).closest<HTMLElement>('[data-slot-key]')?.dataset.slotKey ?? 'floor';
    if (key !== overKey) setOverKey(key);
  }

  function onDropFloor(e: DragEvent<HTMLDivElement>): void {
    const raw = e.dataTransfer.getData(ASSIGN_DRAG_TYPE);
    setOverKey(null);
    if (!raw || !canDrop) return;
    e.preventDefault();
    let load: DragLoad;
    try {
      load = JSON.parse(raw) as DragLoad;
    } catch {
      return; // 다른 데서 끌어온 값 — 무시.
    }
    const key = (e.target as HTMLElement).closest<HTMLElement>('[data-slot-key]')?.dataset.slotKey;
    const slot = key ? (lines.flatMap((l) => l.slots).find((s) => s.key === key) ?? null) : null;
    onDrop(load, slot, { x: e.clientX, y: e.clientY });
  }

  return (
    <section ref={stageRef} className="asg-stage" data-testid={testId} aria-label="배정 무대">
      {header}
      {notes}
      <div
        ref={floorRef}
        className="asg-floor"
        data-dragging={(dragging && canDrop) || undefined}
        data-over={overKey === 'floor' || undefined}
        onDragOver={onDragOver}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOverKey(null);
        }}
        onDrop={onDropFloor}
        data-testid={`${testId}-floor`}
      >
        <div aria-hidden className="asg-spot" />
        {lines.length === 0
          ? empty
          : lines.map((line) => (
              <div key={line.key} className="asg-line" data-testid="assign-stage-line">
                {line.heading && (
                  <div className="asg-line-heading" data-testid="assign-line-heading">
                    {line.heading}
                  </div>
                )}
                <div className="asg-line-head">
                  <b>{line.title}</b>
                  {line.spec}
                  <span className="asg-line-count">{line.count}</span>
                </div>
                <div className="asg-figs">
                  {line.slots.map((s) => (
                    <Figure
                      key={s.key}
                      slot={s}
                      modelCode={line.modelCode}
                      hint={line.hint}
                      over={overKey === s.key}
                    />
                  ))}
                </div>
              </div>
            ))}
      </div>
      {hint && <div className="asg-hint">{hint}</div>}
      {footer}
    </section>
  );
});

function Figure({
  slot: s,
  modelCode,
  hint,
  over,
}: {
  slot: StageSlot;
  modelCode: string | null;
  hint: LineHint | undefined;
  over: boolean;
}) {
  const image = robotImageFor(modelCode);
  const common = {
    className: 'asg-fig',
    'data-slot-key': s.key,
    'data-kind': s.kind,
    'data-tone': s.tone,
    'data-draft': s.draft || undefined,
    'data-hint': s.kind === 'empty' || s.replaceable ? hint : undefined,
    'data-replaceable': s.replaceable || undefined,
    'data-over': over || undefined,
    'data-fly': s.flyKey,
    'data-model': image.kind,
    'data-testid': s.testId,
    title: s.title,
  };
  const body = (
    <>
      <span className="asg-fig-art" aria-hidden>
        <span className="asg-fig-floor" />
        <span className="asg-fig-ring" />
        <img src={image.src} alt="" draggable={false} />
      </span>
      <span className="asg-fig-plate">{s.plate ?? '빈자리'}</span>
      {s.sub != null && <span className="asg-fig-sub">{s.sub}</span>}
      {s.extra}
      {s.draft && <span className="asg-fig-draft">저장 전</span>}
    </>
  );
  if (s.kind === 'empty') {
    return (
      <button type="button" {...common} onClick={s.onClick} aria-label={`빈자리 ${s.title ?? ''}`}>
        {body}
      </button>
    );
  }
  return (
    <div {...common}>
      {body}
      {s.action && <span className="asg-fig-action">{s.action}</span>}
    </div>
  );
}

/** 목록 칩의 가운데 — 누르거나 채울 때 번호표가 여기서 떠난다. 칩의 번호(.asg-sn)로 찾는다. */
export function chipCenters(): Map<string, { x: number; y: number }> {
  const m = new Map<string, { x: number; y: number }>();
  document.querySelectorAll<HTMLElement>('[data-pick-chip]').forEach((el) => {
    const r = el.getBoundingClientRect();
    const label = el.querySelector('.asg-sn')?.textContent ?? '';
    m.set(label, { x: r.left + r.width / 2, y: r.top + r.height / 2 });
  });
  return m;
}

/** 지금 칩 자리를 재어 둔 출발점 — 상태가 바뀌어 칩이 목록에서 빠지기 전에 부른다. */
export function chipFrom(): FlyRequest['from'] {
  const pts = chipCenters();
  return (label) => pts.get(label) ?? null;
}
